import { FIELD_TYPES, type FormHandle } from '@wix/form-viewer';
import {
  FormViewerLean,
  type FormError,
  type FormValues,
  type FormViewerLeanProps,
} from '@wix/form-viewer/lean';
import {
  type WidgetProps,
  useBi,
  usePanorama,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import isEqual from 'lodash/isEqual';
import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { classes } from '../../index.st.css';
import { type ControllerProps } from '../../interfaces';

import { FORM_FIELDS } from './fields';

const RicosViewer = loadable(
  () =>
    import(
      /* webpackChunkName: "form-app-wix-ricos-viewer" */ './wix-ricos-viewer'
    ),
);

export const FormViewer: FC<WidgetProps<ControllerProps>> = ({
  veloValues,
  veloOnValuesChange,
  veloOnSubmit,
  veloOnSubmitSuccess,
  veloOnSubmitFailure,
  veloIsSubmitPending,
  veloOnSubmitValidationFailure,
  veloCurrentStepId,
  veloOnCurrentStepIdChange,
  fireSubmitTrackEvent,
  host,
  ...props
}) => {
  const translation = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { i18n } = useMemo(() => translation, []);
  const [values, setValues] = useState<FormValues>(veloValues);
  const [errors, setErrors] = useState<FormError[]>([]);
  const formViewer = useRef<FormHandle>(null);
  const bi = useBi();
  const panorama = usePanorama();

  const onValidate = useCallback((_errors: FormError[]) => {
    setErrors(_errors);
  }, []);

  const handleFormValuesChange = useCallback(
    (formValues: FormValues) => {
      setValues(formValues);
      veloOnValuesChange?.(formValues);
    },
    [veloOnValuesChange],
  );

  const handleSubmitSuccess = () => {
    veloOnSubmitSuccess();
    fireSubmitTrackEvent(values, FIELD_TYPES.CONTACTS_SUBSCRIBE);
  };

  const handleSubmitFailure = (error: unknown) => {
    veloOnSubmitFailure(error);
  };

  useEffect(() => {
    const shouldUseVeloValues =
      veloValues && !isEqual({}, veloValues) && !isEqual(values, veloValues);

    if (shouldUseVeloValues) {
      setValues(veloValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veloValues]);

  useEffect(() => {
    if (veloIsSubmitPending) {
      const submitForm = async () => {
        const submission = await formViewer.current?.submit();

        if (submission === undefined) {
          veloOnSubmitValidationFailure();
        }
      };

      submitForm();
    }
  }, [veloIsSubmitPending, veloOnSubmitValidationFailure]);

  const hostId = host.id;
  const PortalContainer = useMemo(
    () =>
      ({ children: portalChildren }: { children: React.ReactNode }) =>
        (
          // ensure stylesheet (.compId { ... }) is applied to portal
          <div className={`${classes.formVars} ${hostId} ${hostId}_ooi`}>
            {portalChildren}
          </div>
        ),
    [hostId],
  );

  const viewerProps: FormViewerLeanProps & React.RefAttributes<FormHandle> = {
    i18n,
    ref: formViewer,
    bi,
    formId: props.formId,
    stepId: veloCurrentStepId,
    onStepIdChange: veloOnCurrentStepIdChange,
    values,
    errors,
    onValidate,
    onChange: handleFormValuesChange,
    forcedState: [props.forceView].filter(Boolean),
    fields: FORM_FIELDS,
    RicosViewer,
    onSubmit: veloOnSubmit,
    onSubmitSuccess: handleSubmitSuccess,
    onSubmitFailure: handleSubmitFailure,
    panorama,
    PortalContainer,
  };

  return <FormViewerLean {...viewerProps} />;
};
